import { DndContext, DragEndEvent, DragMoveEvent, DragOverlay, useDroppable } from '@dnd-kit/core';
import { useCallback, useMemo, useRef } from 'react';
import Masonry from 'react-masonry-css';

import { useScrollPagination } from '../../hooks';
import { Image } from '../../ui/Image';
import { Text } from '../../ui/Text';
import { AssetAction, AssetCard, AssetCardProps, TextCard, sourceFallback } from '../AssetCard';
import { ImageCard, ImageCardProps } from '../Card';
import { Portal } from '../Portal';
import { SVGCard, SVGCardProps } from '../SVGCard';
import { StyledDroppableArea, StyledWrapper } from './Grid.style';
import { GridPlaceholder, GridPlaceholderCard } from './GridPlaceholder';
import { useDraggable } from './useDraggable';

export function DroppableArea({ isVisible }: { isVisible: boolean }) {
  const { setNodeRef: droppableRef } = useDroppable({
    id: 'droppable',
    data: {
      accepts: 'canvas',
    },
  });

  return <StyledDroppableArea ref={droppableRef} isVisible={isVisible} />;
}

export interface AssetsItemsProps {
  source: AssetCardProps['source'];
  title: AssetCardProps['title'];
  data: AssetCardProps['data'];
  details?: undefined;
  icon?: string;
  variant?: string;
  active?: boolean;
  badge?: string;
}

export interface SVGItemsProps {
  source: string;
  title: SVGCardProps['title'];
  data: unknown;
  details?: SVGCardProps['details'];
  icon?: string;
  variant?: string;
  active?: boolean;
}

export interface ImageItemsProps {
  source: ImageCardProps['source'];
  title: ImageCardProps['title'];
  data: ImageCardProps['data'];
  details?: undefined;
  icon?: string;
  variant?: string;
  active?: boolean;
  badge?: string;
}

export interface GridProps {
  cardType?: 'text' | 'image' | 'svg' | 'asset';
  assetType?: 'text' | 'image' | 'video' | 'icon' | 'logo';
  items: Array<AssetsItemsProps | SVGItemsProps | ImageItemsProps>;
  actionButtons?: AssetCardProps['actionButtons'];
  onClick?: AssetCardProps['onClick'] | SVGCardProps['onClick'] | ImageCardProps['onClick'];
  onDragging?: (action: AssetAction) => void;
  columns?: number;
  draggable?: boolean;
  isQa?: boolean;
  isLoading?: boolean;
  options?: {
    title?: boolean;
    loadSteps?: number;
  };
  scrollableRef?: HTMLElement | null;
  getPaginationItems?: (
    page: number
  ) => Promise<{ items: GridProps['items']; isLoadedAll: boolean }>;
}

export function Grid({
  cardType = 'asset',
  assetType = 'image',
  items,
  actionButtons,
  onClick = () => {
    return;
  },
  onDragging = () => {
    return;
  },
  options = {
    title: true,
    loadSteps: 20,
  },
  scrollableRef,
  columns = 2,
  draggable = true,
  isQa,
  getPaginationItems,
  isLoading = false,
}: GridProps) {
  const overlayElementRef = useRef<any>(null);
  const onDragOver = useCallback(
    (event: {
      activatorEvent: DragEndEvent['activatorEvent'];
      active: DragEndEvent['active'];
      delta: DragEndEvent['delta'];
      over: DragEndEvent['over'];
    }) => {
      const { activatorEvent, active, delta } = event;
      const mouseEvent = activatorEvent as MouseEvent;

      if (active?.data?.current) {
        onClick({
          action: 'drop',
          source: Array.isArray(active.data.current.source)
            ? active.data.current.source[0]
            : active.data.current.source,
          details: {
            ...active.data.current.details,
            rect: overlayElementRef.current?.getBoundingClientRect(),
            delta: delta,
            mousePosition: {
              x: mouseEvent.x,
              y: mouseEvent.y,
            },
          },
        });
      }
    },
    [onClick, overlayElementRef] // Add overlayElementRef to the dependency array
  );

  const onDragMove = useCallback(
    (event: {
      activatorEvent: DragMoveEvent['activatorEvent'];
      active: DragMoveEvent['active'];
      delta: DragMoveEvent['delta'];
      over: DragMoveEvent['over'];
    }) => {
      const { activatorEvent, active, delta } = event;
      const mouseEvent = activatorEvent as MouseEvent;

      if (active?.data?.current) {
        onDragging({
          action: 'dragging',
          source: Array.isArray(active.data.current.source)
            ? active.data.current.source[0]
            : active.data.current.source,
          details: {
            ...active.data.current.details,
            delta: delta,
            mousePosition: {
              x: mouseEvent.x,
              y: mouseEvent.y,
            },
          },
        });
      }
    },
    [onDragging]
  );
  const { activeItem, isDragging, sensors, handleDragStart, handleDragEnd, handleDragMove } =
    useDraggable({ onClick, onDragOver, onDragMove });

  const isActiveItemText = activeItem?.details?.type === 'Text';
  const Card = useMemo(() => {
    switch (cardType) {
      case 'text':
        return TextCard;
      case 'svg':
        return SVGCard;
      case 'image':
        return ImageCard;
      case 'asset':
      default:
        return AssetCard;
    }
  }, [cardType]);

  const lastCardRef = useRef(null);

  const { displayItems } = useScrollPagination({
    items,
    getPaginationItems,
    lastCardRef,
    scrollableRef,
    loadSteps: options.loadSteps,
  });

  const overlaySource = useMemo((): string => {
    if (!activeItem) return '';

    if (assetType !== 'video') {
      return Array.isArray(activeItem.source) ? activeItem.source[0] : activeItem.source;
    }

    const data = activeItem.details?.data;

    if (data && typeof data === 'object' && !Array.isArray(data) && 'selected_thumbnail' in data) {
      return data.selected_thumbnail as string;
    }

    return '';
  }, [activeItem, assetType]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragMove={handleDragMove}
      autoScroll={false}
    >
      <StyledWrapper>
        <Masonry
          breakpointCols={columns}
          className="masonry-asset-grid"
          columnClassName="masonry-asset-grid-column"
        >
          {displayItems.map((item, index) => (
            <Card
              ref={index === displayItems.length - 1 ? lastCardRef : null}
              key={`grid-${assetType}-${index}-${item.source
                .toString()
                .replace(/\s/g, '-')
                .toLocaleLowerCase()}`}
              source={item.source}
              title={options.title ? item.title : undefined}
              data={item.data}
              details={item.details}
              actionButtons={actionButtons}
              draggable={draggable}
              onClick={onClick}
              isQa={isQa}
              icon={item.icon}
              variant={item.variant}
              active={item.active}
              badge={cardType !== 'svg' && 'badge' in item ? item.badge : undefined}
            />
          ))}

          {isLoading &&
            Array.from({ length: 10 }).map((_, index) => (
              <GridPlaceholderCard key={`grid-placeholder-${index}`} />
            ))}
        </Masonry>
      </StyledWrapper>

      {draggable && (
        <Portal>
          <DragOverlay dropAnimation={null}>
            {activeItem?.source ? (
              overlaySource.startsWith('http') && !isActiveItemText ? (
                <Image
                  source={overlaySource}
                  sourceFallback={sourceFallback}
                  rounded={true}
                  id="react-drag-overlay-element"
                  ref={overlayElementRef}
                />
              ) : (
                <Text size="Body" id="react-drag-overlay-element" ref={overlayElementRef}>
                  {Array.isArray(activeItem.source) ? activeItem.source[0] : activeItem.source}
                </Text>
              )
            ) : null}
          </DragOverlay>

          <DroppableArea isVisible={isDragging} />
        </Portal>
      )}
    </DndContext>
  );
}

Grid.Placeholder = GridPlaceholder;
Grid.PlaceholderCard = GridPlaceholderCard;
